/**
 * 대행사 — 고객 앞에 서는 **판매 주체**(261025-agency-white-label). 권한이 아니라 표시·경계다:
 * 데이터 범위(`brandIds`)는 브랜드 소속이 정하고, 대행사는 「같은 대행사 계정끼리만 소속」이라는 경계를 더한다.
 *
 * 화이트라벨은 대행사의 **선택 기능**이다 — 호스트(`agency_host`)가 있는 대행사만 자기 도메인·브랜딩으로 보인다.
 * 이 파일은 표시값의 모양만 정한다. 판정은 서비스(`domains/agency`)에 있다.
 */
import { z } from 'zod';

/** 계정의 대행사 내 위치 — 결제·대행사 단위 화면의 노출만 가른다(브랜드 안 권한은 소속 역할) */
export const AGENCY_ROLES = ['staff', 'client'] as const;
export type AgencyRole = (typeof AGENCY_ROLES)[number];

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** 화면에 보이는 것 — 비-admin 응답·HTML 주입에 실린다. **다른 대행사의 존재나 우리(liveGPT) 정보를 담지 않는다** */
export const AgencyBranding = z
  .object({
    /** 서비스 이름 — liveGPT 자리에 들어간다 */
    serviceName: z.string().trim().min(1).max(40),
    /**
     * 로고 이미지 주소 — https 주소(대행사가 호스팅) 또는 우리 오리진 경로(`/…`). 없으면 서비스 이름 글자로.
     * CSP 는 이미 `img-src https:` 를 연다(산출물 카드 이미지 때문 — app.ts). 레퍼러는 `<img referrerPolicy>` 가 막는다
     */
    logoPath: z
      .string()
      .max(500)
      .refine((u) => u.startsWith('https://') || (u.startsWith('/') && !u.startsWith('//')), 'https 주소 또는 / 경로')
      .nullable()
      .default(null),
    /** 색 토큰 덮어쓰기. 없으면 기본 팔레트 */
    colors: z.object({ primary: Hex, primaryHover: Hex, heroFrom: Hex, heroTo: Hex }).strict().nullable().default(null),
    /** 고객 지원 연락처 — 화면 하단·도움말 */
    supportTel: z.string().trim().max(30).nullable().default(null),
    supportEmail: z.email().nullable().default(null),
    supportKakaoUrl: z.url().nullable().default(null),
  })
  .strict();
export type AgencyBranding = z.infer<typeof AgencyBranding>;

const DocText = z.string().trim().min(1).max(2000);

/** 약관·처리방침 문서 — 웹 `legal/types.ts` 의 문서 구조와 같은 블록(h3 만 더했다) */
export const AgencyLegalDoc = z
  .object({
    title: z.string().trim().min(1).max(60),
    /** 시행일 YYYY-MM-DD */
    effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    blocks: z
      .array(
        z.discriminatedUnion('kind', [
          z.object({ kind: z.literal('p'), text: DocText }).strict(),
          z.object({ kind: z.literal('h2'), text: DocText }).strict(),
          z.object({ kind: z.literal('h3'), text: DocText }).strict(),
          z.object({ kind: z.literal('ol'), items: z.array(DocText).min(1).max(30) }).strict(),
          z.object({ kind: z.literal('ul'), items: z.array(DocText).min(1).max(30) }).strict(),
          z
            .object({
              kind: z.literal('table'),
              head: z.array(DocText).min(1).max(6),
              rows: z.array(z.array(DocText).min(1).max(6)).min(1).max(40),
            })
            .strict(),
        ]),
      )
      .min(1)
      .max(200),
  })
  .strict();
export type AgencyLegalDoc = z.infer<typeof AgencyLegalDoc>;

/** 법적 고지 — 화면 하단 사업자 정보·약관. 대행사가 판매 주체라 대행사 것이다 */
export const AgencyLegal = z
  .object({
    companyName: z.string().trim().min(1).max(60),
    ceo: z.string().trim().max(30).nullable().default(null),
    businessNo: z.string().trim().max(20).nullable().default(null),
    address: z.string().trim().max(120).nullable().default(null),
    /** 대행사가 준 약관·개인정보처리방침 주소 — 대행사 호스트의 /terms·/privacy 가 여기로 안내한다 */
    termsUrl: z.url().nullable().default(null),
    privacyUrl: z.url().nullable().default(null),
    /**
     * 우리가 대행사 호스트에 게시하는 약관·처리방침 본문 — 있으면 대행사 호스트의 `/terms`·`/privacy` 를 서버가 그린다.
     * 그때 `termsUrl`·`privacyUrl` 은 `https://<대행사 호스트>/terms` 처럼 그 주소를 가리킨다.
     * ★ 웹 주입(`brandingViewOf`)에는 싣지 않는다 — 모든 화면 HTML 에 본문이 붙는다
     */
    termsDoc: AgencyLegalDoc.nullable().default(null),
    privacyDoc: AgencyLegalDoc.nullable().default(null),
  })
  .strict();
export type AgencyLegal = z.infer<typeof AgencyLegal>;

const LandingItem = z
  .object({ title: z.string().trim().min(1).max(40), text: z.string().trim().min(1).max(300) })
  .strict();

/**
 * 대행사 랜딩 — 리포트 앱이 대행사 호스트의 `/` 에서 **서버 렌더**로 내주는 정문 페이지(운영자 결정 2026-10-26).
 * 틀은 하나(`domains/agency/landing.ts`)이고 대행사는 내용만 갖는다. 비면 서비스명·로그인만 있는 기본 랜딩이다.
 * ★ 우리(liveGPT·비하이브) 이야기를 쓰지 않는다 — 판매 주체는 대행사다
 */
export const AgencyLanding = z
  .object({
    /** 제목 위 작은 글씨(예: ChatGPT · Gemini · Perplexity) */
    eyebrow: z.string().trim().max(60).nullable().default(null),
    /** 첫 화면 큰 제목 — 없으면 서비스 이름 */
    headline: z.string().trim().min(1).max(80).nullable().default(null),
    /** 제목 아래 한두 문장 */
    intro: z.string().trim().max(400).nullable().default(null),
    /** 숫자 띠 — 대행사 실적(예: 2017~ 바이럴 경력). 대행사가 근거를 가진 숫자만 */
    stats: z
      .array(z.object({ value: z.string().trim().min(1).max(12), label: z.string().trim().min(1).max(30) }).strict())
      .max(4)
      .default([]),
    /** 「이런 상황이라면」 — 고객의 고민 카드 */
    pains: z.array(LandingItem).max(6).default([]),
    /** 일반 대행과 비교 — 행 이름 · 일반 대행 · 우리. 표 머리는 「일반 글 발행 대행」·서비스 이름 */
    compare: z
      .array(
        z
          .object({
            row: z.string().trim().min(1).max(20),
            other: z.string().trim().min(1).max(80),
            ours: z.string().trim().min(1).max(80),
          })
          .strict(),
      )
      .max(8)
      .default([]),
    /** 진행 단계 — 순서대로 */
    steps: z.array(LandingItem).max(6).default([]),
    /** 무엇을 해 주는가 — 카드 */
    features: z.array(LandingItem).max(6).default([]),
    /** 사례 — 카드. 고객 이름은 동의를 받은 것만 */
    cases: z.array(LandingItem).max(6).default([]),
    /** 자주 묻는 질문 — 펼침(JS 없음) + FAQPage 구조화 데이터 */
    faq: z
      .array(z.object({ q: z.string().trim().min(1).max(80), a: z.string().trim().min(1).max(600) }).strict())
      .max(8)
      .default([]),
    /** 상담 안내 한 줄(예: 문의만 남기셔도 무료로 진단해 드립니다) */
    contactNote: z.string().trim().max(160).nullable().default(null),
    /** 상담 신청 폼 주소(대행사 사이트) — 있으면 주 상담 버튼이 이리로 간다 */
    contactUrl: z.url().nullable().default(null),
    /** 주 상담 버튼 글자 — 없으면 「상담 신청」 */
    ctaLabel: z.string().trim().max(24).nullable().default(null),
  })
  .strict();
export type AgencyLanding = z.infer<typeof AgencyLanding>;

/** 웹이 받는 화이트라벨 표시값 한 벌 — 서버가 `index.html` 에 주입한다. null 이면 liveGPT 기본값 */
export const BrandingView = z
  .object({
    branding: AgencyBranding,
    legal: AgencyLegal,
  })
  .strict();
export type BrandingView = z.infer<typeof BrandingView>;
